import React, { useEffect, useRef, useState, useCallback } from "react";
import { Reorder, useDragControls } from "framer-motion";
import { api } from "../../lib/api";
import { Avatar, PriorityBadge, Modal, Field, ConfirmModal } from "../common";
import { Clapperboard, Plus, GripVertical, Trash2, Pencil, Send, Paperclip, X, Radio, Check, Lock, Users, Megaphone } from "lucide-react";

const PRIORITIES = ["NORMAL", "INFO", "IMPORTANT", "ALERTE", "URGENT", "FLASH"];

function chanIcon(t) {
  if (t === "private") return <Lock size={11} />;
  if (t === "group") return <Users size={11} />;
  if (t === "info_descending" || t === "ad") return <Megaphone size={11} />;
  return <Radio size={11} />;
}

/* ------------------------------------------------------------------ Cue editor */
function CueEditor({ open, onClose, cid, sceneId, channels, editing, onSaved }) {
  const [channelId, setChannelId] = useState("");
  const [senderId, setSenderId] = useState("");
  const [content, setContent] = useState("");
  const [priority, setPriority] = useState("NORMAL");
  const [file, setFile] = useState(null);
  const [existingAtt, setExistingAtt] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const fileRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setErr(""); setFile(null);
    if (editing) {
      setChannelId(editing.channel_id);
      setSenderId(editing.sender_type === "system" ? "__system__" : editing.sender_identity_id || "");
      setContent(editing.content || "");
      setPriority(editing.priority || "NORMAL");
      setExistingAtt(editing.attachment || null);
    } else {
      setChannelId(""); setSenderId(""); setContent(""); setPriority("NORMAL"); setExistingAtt(null);
    }
  }, [open, editing]);

  const selChannel = channels.find((c) => c.id === channelId);
  const npcParts = (selChannel?.participants_view || []).filter((p) => p.type === "npc");
  const isFeed = selChannel && (selChannel.type === "info_descending" || selChannel.type === "info_interactive" || selChannel.type === "ad");

  useEffect(() => {
    if (!selChannel) return;
    if (senderId === "__system__") return;
    if (!npcParts.find((p) => p.id === senderId)) setSenderId(npcParts[0]?.id || "");
    // eslint-disable-next-line
  }, [channelId]);

  async function save() {
    if (!channelId) { setErr("Choisissez un canal"); return; }
    if (!senderId) { setErr("Choisissez une identité émettrice"); return; }
    if (!content.trim() && !file && !existingAtt) { setErr("Contenu ou pièce jointe requis"); return; }
    setBusy(true); setErr("");
    try {
      let attachment_id = existingAtt?.id || null;
      let message_type = existingAtt ? (existingAtt.content_type?.startsWith("image/") ? "image" : "file") : "text";
      if (file) {
        const fd = new FormData(); fd.append("file", file);
        const up = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        attachment_id = up.data.id;
        message_type = (up.data.content_type || "").startsWith("image/") ? "image" : "file";
      }
      const body = {
        channel_id: channelId, scene_id: sceneId,
        sender_type: senderId === "__system__" ? "system" : "npc",
        sender_identity_id: senderId === "__system__" ? null : senderId,
        content: content.trim(), message_type, attachment_id,
        priority: isFeed ? priority : "NORMAL",
      };
      if (editing) await api.patch(`/cues/${editing.id}`, body);
      else await api.post(`/campaigns/${cid}/cues`, body);
      onSaved();
      onClose();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Erreur réseau");
    } finally { setBusy(false); }
  }

  return (
    <Modal open={open} onClose={onClose} title={editing ? "MODIFIER LA RÉPLIQUE" : "NOUVELLE RÉPLIQUE"} width={520}>
      <Field label="CANAL CIBLE">
        <select data-testid="cue-channel-select" className="input font-mono text-xs" value={channelId} onChange={(e) => setChannelId(e.target.value)}>
          <option value="">— CHOISIR UN CANAL —</option>
          {channels.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </Field>
      <Field label="PARLER EN TANT QUE">
        <select data-testid="cue-sender-select" className="input font-mono text-xs" value={senderId} onChange={(e) => setSenderId(e.target.value)} disabled={!selChannel}>
          {npcParts.length === 0 && <option value="">— AUCUN PNJ DANS CE CANAL —</option>}
          {npcParts.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          <option value="__system__">SYSTÈME</option>
        </select>
      </Field>
      {isFeed && (
        <Field label="PRIORITÉ">
          <select data-testid="cue-priority-select" className="input font-mono text-xs" value={priority} onChange={(e) => setPriority(e.target.value)}>
            {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </Field>
      )}
      <Field label="MESSAGE">
        <textarea data-testid="cue-content-input" rows={4} className="input resize-none font-sans" placeholder="Texte de la réplique…" value={content} onChange={(e) => setContent(e.target.value)} />
      </Field>
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <input ref={fileRef} type="file" className="hidden" onChange={(e) => setFile(e.target.files[0] || null)} />
        <button data-testid="cue-attach-button" onClick={() => fileRef.current?.click()} className="btn btn-ghost text-[10px] flex items-center gap-1"><Paperclip size={12} /> PIÈCE JOINTE</button>
        {(file || existingAtt) && (
          <span className="flex items-center gap-1 text-xs font-mono" style={{ color: "var(--r-primary)" }}>
            <Paperclip size={11} /> {file ? file.name : existingAtt.filename}
            <button onClick={() => { setFile(null); setExistingAtt(null); }}><X size={12} /></button>
          </span>
        )}
      </div>
      {err && <div data-testid="cue-error" className="font-mono text-xs mb-3 py-2 px-3" style={{ color: "var(--r-alert)", border: "1px solid var(--r-alert)" }}>✕ {err}</div>}
      <button data-testid="cue-save-button" disabled={busy} onClick={save} className="btn btn-solid w-full flex items-center justify-center gap-2">
        <Check size={14} /> {busy ? "ENREGISTREMENT…" : (editing ? "ENREGISTRER" : "AJOUTER À LA FILE")}
      </button>
    </Modal>
  );
}

/* ------------------------------------------------------------------ Cue row */
function CueRow({ cue, onPersist, onDiffuse, onEdit, onDelete }) {
  const controls = useDragControls();
  const [diffusing, setDiffusing] = useState(false);
  async function diffuse() { setDiffusing(true); try { await onDiffuse(cue); } finally { setDiffusing(false); } }
  return (
    <Reorder.Item
      value={cue}
      dragListener={false}
      dragControls={controls}
      onDragEnd={onPersist}
      data-testid={`cue-item-${cue.id}`}
      className="frame p-2.5 flex items-center gap-2.5 mb-2"
      style={{ background: "var(--r-panel)" }}
      whileDrag={{ scale: 1.02, boxShadow: "0 8px 24px rgba(0,0,0,0.5)" }}
    >
      <button onPointerDown={(e) => controls.start(e)} className="cursor-grab active:cursor-grabbing text-[color:var(--r-text-muted)] touch-none shrink-0" title="Glisser pour réordonner" data-testid={`cue-drag-${cue.id}`}>
        <GripVertical size={16} />
      </button>
      <Avatar src={cue.sender_avatar} name={cue.sender_name} size={34} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="font-semibold text-sm truncate" style={{ color: "var(--r-secondary)" }}>{cue.sender_name}</span>
          <span className="sys-label flex items-center gap-1" style={{ textTransform: "none" }}>{chanIcon(cue.channel_type)} {cue.channel_name}</span>
          {cue.priority !== "NORMAL" && <PriorityBadge level={cue.priority} />}
        </div>
        <div className="text-sm mt-0.5 break-words" style={{ color: "var(--r-text)" }}>
          {cue.content || <span style={{ color: "var(--r-text-muted)" }} className="italic">[pièce jointe]</span>}
          {cue.attachment && <span className="ml-1.5 inline-flex items-center gap-1 text-xs font-mono" style={{ color: "var(--r-primary)" }}><Paperclip size={10} /> {cue.attachment.filename}</span>}
        </div>
      </div>
      <button data-testid={`cue-edit-${cue.id}`} onClick={() => onEdit(cue)} className="text-[color:var(--r-text-muted)] hover:text-[color:var(--r-primary)] shrink-0" title="Modifier"><Pencil size={14} /></button>
      <button data-testid={`cue-delete-${cue.id}`} onClick={() => onDelete(cue)} className="text-[color:var(--r-text-muted)] hover:text-[color:var(--r-alert)] shrink-0" title="Supprimer"><Trash2 size={14} /></button>
      <button data-testid={`cue-diffuse-${cue.id}`} onClick={diffuse} disabled={diffusing} className="btn btn-solid text-[10px] flex items-center gap-1 shrink-0 tap" title="Diffuser maintenant">
        <Send size={12} /> {diffusing ? "…" : "DIFFUSER"}
      </button>
    </Reorder.Item>
  );
}

/* ------------------------------------------------------------------ Scene card */
function SceneCard({ scene, onReload, onDelete, onAddCue, onEditCue, onDeleteCue }) {
  const controls = useDragControls();
  const [cues, setCues] = useState(scene.cues);
  const cuesRef = useRef(scene.cues);
  const [editingName, setEditingName] = useState(false);
  const [name, setName] = useState(scene.name);

  useEffect(() => { setCues(scene.cues); cuesRef.current = scene.cues; }, [scene.cues]);
  useEffect(() => { setName(scene.name); }, [scene.name]);

  function handleReorder(newOrder) { setCues(newOrder); cuesRef.current = newOrder; }
  async function persistCues() {
    await api.post(`/scenes/${scene.id}/cues/reorder`, { order: cuesRef.current.map((c) => c.id) });
    onReload();
  }
  async function saveName() {
    setEditingName(false);
    if (name.trim() && name.trim() !== scene.name) { await api.patch(`/scenes/${scene.id}`, { name: name.trim() }); onReload(); }
  }
  async function diffuseCue(cue) {
    await api.post(`/cues/${cue.id}/diffuse`);
    onReload();
  }

  return (
    <Reorder.Item
      value={scene}
      dragListener={false}
      dragControls={controls}
      data-testid={`scene-card-${scene.id}`}
      className="frame p-3 mb-4"
      style={{ background: "color-mix(in srgb, var(--r-panel) 70%, transparent)" }}
      whileDrag={{ scale: 1.01 }}
    >
      <div className="flex items-center gap-2 mb-3">
        <button onPointerDown={(e) => controls.start(e)} className="cursor-grab active:cursor-grabbing text-[color:var(--r-text-muted)] touch-none" title="Glisser la scène" data-testid={`scene-drag-${scene.id}`}>
          <GripVertical size={18} />
        </button>
        <Clapperboard size={15} style={{ color: "var(--r-primary)" }} />
        {editingName ? (
          <input data-testid={`scene-name-input-${scene.id}`} autoFocus className="input font-title font-bold text-sm" style={{ width: "auto", flex: 1 }} value={name} onChange={(e) => setName(e.target.value)} onBlur={saveName} onKeyDown={(e) => { if (e.key === "Enter") saveName(); }} />
        ) : (
          <button data-testid={`scene-name-${scene.id}`} onClick={() => setEditingName(true)} className="font-title font-bold text-base tracking-tight flex-1 text-left truncate hover:opacity-80">{scene.name}</button>
        )}
        <span className="sys-label shrink-0">{cues.length} RÉPL.</span>
        <button data-testid={`scene-add-cue-${scene.id}`} onClick={() => onAddCue(scene.id)} className="btn btn-ghost text-[10px] flex items-center gap-1 shrink-0"><Plus size={12} /> RÉPLIQUE</button>
        <button data-testid={`scene-delete-${scene.id}`} onClick={() => onDelete(scene)} className="text-[color:var(--r-text-muted)] hover:text-[color:var(--r-alert)] shrink-0" title="Supprimer la scène"><Trash2 size={15} /></button>
      </div>
      {cues.length === 0 ? (
        <div className="sys-label px-2 py-3">AUCUNE RÉPLIQUE — AJOUTEZ-EN UNE</div>
      ) : (
        <Reorder.Group axis="y" values={cues} onReorder={handleReorder} className="pl-1">
          {cues.map((c) => (
            <CueRow key={c.id} cue={c} onPersist={persistCues} onDiffuse={diffuseCue} onEdit={onEditCue} onDelete={onDeleteCue} />
          ))}
        </Reorder.Group>
      )}
    </Reorder.Item>
  );
}

/* ------------------------------------------------------------------ Main */
export default function Regie({ regie, cid, channels, onReload }) {
  const [scenes, setScenes] = useState(regie.scenes);
  const scenesRef = useRef(regie.scenes);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorScene, setEditorScene] = useState(null);
  const [editorCue, setEditorCue] = useState(null);
  const [confirmScene, setConfirmScene] = useState(null);
  const [confirmCue, setConfirmCue] = useState(null);

  useEffect(() => { setScenes(regie.scenes); scenesRef.current = regie.scenes; }, [regie.scenes]);

  function handleSceneReorder(newOrder) { setScenes(newOrder); scenesRef.current = newOrder; }
  const persistScenes = useCallback(async () => {
    await api.post(`/campaigns/${cid}/scenes/reorder`, { order: scenesRef.current.map((s) => s.id) });
    onReload();
  }, [cid, onReload]);

  async function addScene() {
    await api.post(`/campaigns/${cid}/scenes`, { name: `ACTE ${scenes.length + 1}` });
    onReload();
  }
  function openAddCue(sceneId) { setEditorScene(sceneId); setEditorCue(null); setEditorOpen(true); }
  function openEditCue(cue) { setEditorScene(cue.scene_id); setEditorCue(cue); setEditorOpen(true); }

  async function deleteScene() {
    await api.delete(`/scenes/${confirmScene.id}`);
    setConfirmScene(null); onReload();
  }
  async function deleteCue() {
    await api.delete(`/cues/${confirmCue.id}`);
    setConfirmCue(null); onReload();
  }

  return (    <div className="flex-1 overflow-y-auto p-5" style={{ background: "var(--r-void)" }} data-testid="regie-view">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-5">
          <div>
            <div className="sys-label mb-1 flex items-center gap-2"><Clapperboard size={12} /> MODE RÉGIE</div>
            <h2 className="font-title font-bold text-xl tracking-tight">SCÉNARIO — FILE DE RÉPLIQUES</h2>
            <p className="text-xs mt-1" style={{ color: "var(--r-text-muted)" }}>Préparez vos répliques par scène, réordonnez par glisser-déposer, puis diffusez-les d'un tap au bon moment.</p>
          </div>
          <button data-testid="add-scene-button" onClick={addScene} className="btn btn-solid text-[11px] flex items-center gap-1 shrink-0"><Plus size={13} /> NOUVELLE SCÈNE</button>
        </div>

        {scenes.length === 0 ? (
          <div className="frame p-8 text-center">
            <Clapperboard size={30} style={{ color: "var(--r-primary)", opacity: 0.5 }} className="mx-auto" />
            <div className="sys-label mt-3">AUCUNE SCÈNE</div>
            <p className="text-sm mt-2" style={{ color: "var(--r-text-muted)" }}>Créez une première scène pour commencer à scénariser votre partie.</p>
          </div>
        ) : (
          <Reorder.Group axis="y" values={scenes} onReorder={handleSceneReorder}>
            {scenes.map((s) => (
              <div key={s.id} onPointerUp={persistScenes}>
                <SceneCard
                  scene={s}
                  onReload={onReload}
                  onDelete={(sc) => setConfirmScene(sc)}
                  onAddCue={openAddCue}
                  onEditCue={openEditCue}
                  onDeleteCue={(cue) => setConfirmCue(cue)}
                />
              </div>
            ))}
          </Reorder.Group>
        )}
      </div>

      <CueEditor
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        cid={cid}
        sceneId={editorScene}
        channels={channels}
        editing={editorCue}
        onSaved={onReload}
      />
      <ConfirmModal
        open={!!confirmScene}
        onClose={() => setConfirmScene(null)}
        onConfirm={deleteScene}
        title="SUPPRIMER LA SCÈNE"
        confirmLabel="SUPPRIMER"
        message={`Supprimer « ${confirmScene?.name} » et toutes ses répliques en attente ? Cette action est irréversible.`}
      />
      <ConfirmModal
        open={!!confirmCue}
        onClose={() => setConfirmCue(null)}
        onConfirm={deleteCue}
        title="SUPPRIMER LA RÉPLIQUE"
        confirmLabel="SUPPRIMER"
        message="Retirer cette réplique de la file ?"
      />
    </div>
  );
}
